import { forwardRef } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Controller, useFormContext } from "react-hook-form";
import { addMinutes, format } from "date-fns";
import type DateWithTimeRangeViewModel from "../../../types/viewModels/DateWithTimeRangeViewModel";
import {
  normalizeTime,
  timeStringToDate,
} from "../../../helpers/dateTimeHelper";

type DatePickerInputProps = {
  className?: string;
  onClick?: () => void;
};

export const DatePickerInput = forwardRef<HTMLDivElement, DatePickerInputProps>(
  ({ className, onClick }, ref) => {
    const { watch } = useFormContext<DateWithTimeRangeViewModel>();

    const scheduledDate = watch("ScheduledDate");
    const scheduledStartTime = watch("ScheduledStartTime");
    const scheduledEndTime = watch("ScheduledEndTime");

    const dateNow = new Date();

    const dateString = scheduledDate
      ? scheduledDate
      : format(dateNow, "MMMM dd, yyyy");

    const startTime = scheduledStartTime
      ? normalizeTime(scheduledStartTime)
      : format(dateNow, "HH:mm");

    const endTime = scheduledEndTime
      ? normalizeTime(scheduledEndTime)
      : format(addMinutes(dateNow, 30), "HH:mm");

    const label = `${dateString}  ${startTime} - ${endTime}`;

    return (
      <div className={className} onClick={onClick} ref={ref}>
        {label}
      </div>
    );
  }
);

export const TimeRangePicker: React.FC = () => {
  const { control } = useFormContext<DateWithTimeRangeViewModel>();

  return (
    <div className="flex justify-between gap-2 clear-both mb-2 mt-2 px-3 py-2 border-t border-b border-gray-200">
      {/* START TIME */}
      <div className="">
        <label className="mb-1 block text-xs font-medium text-gray-600">
          Start Time
        </label>

        <Controller
          name="ScheduledStartTime"
          control={control}
          render={({ field }) => (
            <DatePicker
              selected={timeStringToDate(field.value)}
              onChange={(date: Date | null) =>
                field.onChange(date ? format(date, "HH:mm") : null)
              }
              showTimeSelect
              showTimeSelectOnly
              timeIntervals={15}
              dateFormat="HH:mm"
              timeFormat="HH:mm"
              timeCaption="Start"
              placeholderText="Start time"
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
            />
          )}
        />
      </div>

      {/* END TIME */}
      <div className="">
        <label className="mb-1 block text-xs font-medium text-gray-600">
          End Time
        </label>
        <Controller
          name="ScheduledEndTime"
          control={control}
          render={({ field }) => (
            <DatePicker
              selected={timeStringToDate(field.value)}
              onChange={(date: Date | null) =>
                field.onChange(date ? format(date, "HH:mm") : null)
              }
              showTimeSelect
              showTimeSelectOnly
              timeIntervals={15}
              dateFormat="HH:mm"
              timeFormat="HH:mm"
              timeCaption="End"
              placeholderText="End time"
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
            />
          )}
        />
      </div>
    </div>
  );
};

export const DateWithTimeRangePicker: React.FC = () => {
  const { control } = useFormContext<DateWithTimeRangeViewModel>();

  return (
    <div className="">
      <Controller
        name="ScheduledDate"
        control={control}
        render={({ field }) => (
          <DatePicker
            selected={field.value ? new Date(field.value) : null}
            onChange={(date: Date | null) =>
              field.onChange(date ? format(date, "yyyy-MM-dd") : null)
            }
            peekNextMonth
            showMonthDropdown
            showYearDropdown
            dropdownMode="select"
            dateFormat="yyyy-MM-dd"
            placeholderText="Select date"
            customInput={<DatePickerInput className="text-sm" />}
            calendarClassName="yt-datepicker"
          >
            <TimeRangePicker />
          </DatePicker>
        )}
      />
    </div>
  );
};
